"use client";

import { Icon, ospThemes } from "@ospweb/sdk-ui";
import React, { useMemo } from "react";
import { cn } from "utils";
import { EdgenModalCreateAble } from "../../EdgenModal";
import NextImage from "../../NextImage";
import type {
  RewardPopupCreateAbleProps,
  RewardPopupProps,
  RewardPopupResult,
} from "./types";

import { I18nUtil } from "@utils/i18n";
import type { Reward } from "@/src/hooks/useQueryRewards";
import Translations from "@translate/components/modal/RewardPopup/RewardPopup";

const I18n = I18nUtil.createScoped(Translations);

// Presentational component for REWARDS type
const RewardRewards: React.FC<{ reward: Reward }> = ({ reward }) => (
  <>
    <span className="body_m text-content_tertiary">
      {I18n.t("Edgen Subscribe")}
    </span>
    <span className="title_m text-content_primary">{reward.value}</span>
  </>
);

// Presentational component for AURA type
const RewardAura: React.FC<{ reward: Reward }> = ({ reward }) => (
  <>
    <span className="body_m text-content_tertiary">{I18n.t("Aura")}</span>
    <div className="flex flex-row items-center gap-1">
      <NextImage
        src="/assets/images/diamond.png"
        alt="Aura"
        className="w-4 h-4"
      />
      <span className="title_m text-content_primary">{reward.value}</span>
    </div>
  </>
);

// Mapper to select the appropriate component based on reward type
const getRewardComponent = (reward: Reward) => {
  switch (reward.type) {
    case "REWARDS":
      return <RewardRewards reward={reward} />;
    case "AURA":
      return <RewardAura reward={reward} />;
    default:
      // Fallback for unknown reward types
      return (
        <>
          <span className="body_m text-content_tertiary">
            {I18n.t("Unknown")}
          </span>
          <span className="title_m text-content_primary">{reward.value}</span>
        </>
      );
  }
};

/**
 * RewardPopup Component
 * A popup component that displays reward information with celebration animation
 */
const RewardPopup: React.FC<RewardPopupProps> = ({
  className,
  onClose,
  rewards,
}) => {
  const items = useMemo(() => {
    return rewards?.map((reward) => (
      <div
        key={reward.orderId}
        className="flex flex-row justify-between items-center"
      >
        {getRewardComponent(reward)}
      </div>
    ));
  }, [rewards]);

  return (
    <div className="relative md:rounded-2xl overflow-hidden">
      <NextImage
        src="/assets/images/reward-pop-bg.png"
        alt="Background"
        className="w-full absolute top-0 left-0 right-0 z-0"
        height={114}
        width={352}
      />
      <div
        className={cn(
          "relative flex flex-col p-6 items-center z-10",
          className,
        )}
      >
        <div className="flex flex-row items-center gap-0.5">
          <Icon name="pricing" size={34} className="text-gradient" />
          <picture>
            <source
              srcSet="/edgen_logo_dark.svg"
              type="image/svg+xml"
              media="(min-width: 960px)"
            />
            <NextImage
              src="/edgen_logo_light.svg"
              alt="Aura Logo"
              width={150}
              height={42}
            />
          </picture>
        </div>
        <span className="headline_regular_s text-content_primary mt-4">
          {}
          {I18n.t("Congrats, you've received")}
        </span>

        <div className="relative w-full mt-8">
          <div className="w-full rounded-3 pt-8 px-4 pb-4 border-solid border-[1px] border-background_white_overlay10 min-h-17">
            {items}
          </div>

          <div className="absolute top--4 left-4 w-fit px-4 py-1.5 rounded-4 border-solid border-[1px] bg-background_solid_secondary flex flex-row items-center justify-center gap-1 border-border_white_transparent10">
            <NextImage src="/assets/images/party.svg" width={16} height={16} />
            <span className="body_medium_m text-content_primary">
              {I18n.t("Rewards")}
            </span>
          </div>
        </div>

        <button
          type="button"
          data-openreplay-label={`RewardPopup · Button | ${I18n.t("Get started")}`}
          className={cn(
            "button md_button primary_button w-full button_m h-11 z-100 mt-6",
          )}
          onClick={onClose}
        >
          {I18n.t("Get started")}
        </button>
      </div>
    </div>
  );
};

/**
 * 可创建的 RewardPopup
 * 支持外部随用随调，无需绑定在组件上
 */
export const RewardPopupCreateAble = async (
  props: RewardPopupCreateAbleProps,
): Promise<RewardPopupResult> => {
  const { modalProps, ...otherProps } = props;

  return new Promise((resolve) => {
    EdgenModalCreateAble.call({
      children: (
        <RewardPopup
          {...otherProps}
          onClose={() => {
            EdgenModalCreateAble.end(false);
            resolve({
              action: "close",
            });
          }}
        />
      ),
      containerClassName: "!md:w-[352px] !md:rounded-2xl",
      contentStyle: {
        borderRadius: 16,
        backgroundColor: ospThemes.colors.background_solid_secondary,
        width: "100%",
        padding: 0,
      },
      titleStyle: {
        minHeight: 0,
      },
      centered: true,
      showClose: false,
      destroyOnClose: true,
      ...modalProps,
    });
  });
};
